import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import {
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
} from "@mui/material";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { Box } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
    addMediaAsync,
    compressAsync,
    CompressionLevel,
    onCompressionLevelChanged,
    selectState,
} from "./CompressionSlice";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { HomeCard } from "../../components/home_card/HomeCard";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export function Compression() {
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectState);
    const { t } = useTranslation();

    return (
        <SideMenuPageContainer
            pageTitle={t("compression.title")}
            pageSeoTitle={t("compression.seoTitle")}
            pageDescription={t("compression.description")}
        >
            <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
                <Grid2
                    container
                    spacing={2}
                    sx={{ mt: 2 }}
                    columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
                >
                    <Grid2 xs={12}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                            {t("compression.mainHeading")}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            <b style={{ color: "#FFD9A8" }}>{t("compression.step1")}</b> {t("compression.step1Description")}
                            <br />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Stack
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Button
                                component="label"
                                variant="contained"
                                startIcon={<CloudUploadIcon sx={{ color: "#FFD9A8" }} />}
                                onChange={(e: any) => {
                                    const files = e.target.files;
                                    if (files) {
                                        dispatch(addMediaAsync(files[0]));
                                    }
                                }}
                            >
                                <Typography>{t("compression.selectButton")}</Typography>
                                <VisuallyHiddenInput
                                    type="file"
                                    accept="image/*,video/mp4"
                                />
                            </Button>
                        </Stack>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Typography
                            sx={{
                                mb: 1,
                                display:
                                    state.mediaToCompress.length >= 0 ? "flex" : "none",
                            }}
                        >
                            {t("compression.selectedFile")}
                        </Typography>
                        {state.mediaToCompress.map((media, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography sx={{ width: 300 }}>{media.name}</Typography>
                            </Box>
                        ))}
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            <b style={{ color: "#FFD9A8" }}>{t("compression.step2")}</b> {t("compression.step2Description")}
                            <br />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Stack
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <ToggleButtonGroup
                                color="secondary"
                                value={state.compressionLevel}
                                exclusive
                                onChange={(
                                    event: React.MouseEvent<HTMLElement>,
                                    newValue: string | null
                                ) => {
                                    dispatch(
                                        onCompressionLevelChanged(
                                            Number(newValue) as CompressionLevel
                                        )
                                    );
                                }}
                                aria-label="text alignment"
                            >
                                <ToggleButton
                                    value={CompressionLevel.minimum}
                                    aria-label="left aligned"
                                >
                                    <Typography>{t("compression.least")}</Typography>
                                </ToggleButton>
                                <ToggleButton
                                    value={CompressionLevel.default}
                                    aria-label="left aligned"
                                >
                                    <Typography>{t("compression.default")}</Typography>
                                </ToggleButton>
                                <ToggleButton
                                    value={CompressionLevel.maximum}
                                    aria-label="centered"
                                >
                                    <Typography>{t("compression.most")}</Typography>
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Typography sx={{
                                mb: 1,
                                mt: 1,
                                display:
                                    state.compressionLevel == CompressionLevel.minimum ? "flex" : "none",
                            }}>
                                <b style={{ color: "#FFD9A8" }}> {t("compression.leastDescription")}</b>
                            </Typography>

                            <Typography sx={{
                                mb: 1,
                                mt: 1,
                                display:
                                    state.compressionLevel == CompressionLevel.default ? "flex" : "none",
                            }}>
                                <b style={{ color: "#FFD9A8" }}> {t("compression.defaultDescription")}</b>
                            </Typography>


                            <Typography sx={{
                                mb: 1,
                                mt: 1,
                                display:
                                    state.compressionLevel == CompressionLevel.maximum ? "flex" : "none",
                            }}>
                                <b style={{ color: "#FFD9A8" }}> {t("compression.mostDescription")}</b>
                            </Typography>

                        </Stack>
                    </Grid2>


                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            <b style={{ color: "#FFD9A8" }}>{t("compression.step3")}</b> {t("compression.step3Description")}
                            <br />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Stack
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Typography
                                sx={{
                                    display:
                                        state.mediaToCompress.length < 1 ? "flex" : "none",
                                }}
                            >
                                {t("compression.selectFileFirst")}
                            </Typography>

                            <CircularProgress sx={{
                                display:
                                    state.compressionInProgress == true ? "flex" : "none",
                            }} color="secondary" />

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    display:
                                        state.compressionInProgress == true ? "flex" : "none",
                                }}
                            >
                                {t("compression.compressingMessage")}
                            </Typography>

                            <Button
                                sx={{
                                    display:
                                        state.mediaToCompress.length >= 1 && state.compressionInProgress == false ? "flex" : "none",
                                }}
                                component="label"
                                variant="contained"
                                startIcon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
                                onClick={(e: any) => dispatch(compressAsync())}
                            >
                                <Typography>{t("compression.compressButton")}</Typography>
                            </Button>
                        </Stack>
                    </Grid2>
                    <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
                        <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
                    </Grid2>
                    <Grid2 xs={12}>
                        <AdSense
                            adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                            adFormat="fluid"
                            adLayoutKey="-fb+5w+4e-db+86"
                            sx={{ mb: 3 }}
                            />
                        <Typography variant="h3" sx={{ mb: 2 }}>
                            {t("compression.content.compressAnyHeading")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {t("compression.content.compressAnyDescription")}
                        </Typography>
                        <Typography variant="h3" sx={{ mb: 2 }}>
                            {t("compression.content.privacyHeading")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {t("compression.content.privacyDescription")}
                        </Typography>

                    </Grid2>
                    <Grid2 xs={12}>
                        <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
                            {t("compression.related")}
                        </Typography>
                        <Grid2 xs={3}>
                            <HomeCard
                                languageResource="imageConverter"
                                link="/ImageConverter"
                                smallCard={true}
                                image={ImageConverterImage}
                            />
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Box>
        </SideMenuPageContainer>
    );
}
